import { InputHTMLAttributes, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import styled from "styled-components";
import { ErrorIcon } from "./icon/ErrorIcon";
import { COLOR_BLACK, COLOR_LIGHT_RED, COLOR_ORANGE } from "../../constants/color";


type Props = {
  name: string;
  uuid?: string;
  label?: string;
  type?: InputHTMLAttributes<HTMLInputElement>["type"] | "textarea";
  placeholder?: string;
  defaultValue?: string;
  value?: string;
  onChange?: (value: string) => void;
  required?: boolean;
  disabled?: boolean;
  autoFocus?: boolean;
  hasError?: boolean;
  errorMessage?: string;
  helperText?: string;
  autoComplete?: string;
  clearable?: boolean;
  maxLength?: number;
  className?: string;
  height?: string;
  width?: string;
  textareaHeight?: string | number;
};

export const Input: React.FC<Props> = ({
  name,
  uuid,
  label,
  type,
  placeholder,
  defaultValue,
  value,
  onChange,
  required,
  disabled,
  autoFocus,
  hasError,
  errorMessage,
  helperText,
  autoComplete,
  clearable,
  maxLength,
  className,
  height,
  width,
  textareaHeight,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const formContext = useFormContext();
  const { register } = formContext ?? {};

  const [showPassword] = useState<boolean>(false);

  const isTextarea = type === "textarea";

  return (
    <StyledContainer className={className}>
      <StyledHeader>
        {label && (
          <StyledLabel>
            {required ? (
              <>
                {label.replace(' *', '')} <StyledRequiredAsterisk>*</StyledRequiredAsterisk>
              </>
            ) : (
              label
            )}
          </StyledLabel>
        )}
      </StyledHeader>

      <StyledInputContainer>
        {isTextarea ? (
          <StyledTextarea
            id={uuid ? `input-${uuid}` : undefined}
            {...register?.(name)}
            ref={register?.(name).ref ?? inputRef}
            onChange={(e) => {
              if (onChange) onChange(e.target.value);
              register?.(name).onChange(e);
            }}
            placeholder={placeholder}
            defaultValue={defaultValue}
            value={value}
            disabled={disabled}
            maxLength={maxLength}
            autoComplete={autoComplete ?? "off"}
            autoFocus={autoFocus}
            $hasError={hasError}
            rows={4}
            $width={width}
            $height={height}
          />
        ) : (
          <StyledInput
            id={uuid ? `input-${uuid}` : undefined}
            {...register?.(name)}
            ref={register?.(name).ref ?? inputRef}
            onChange={(e) => {
              if (onChange) onChange(e.target.value);
              register?.(name).onChange(e);
            }}
            type={showPassword ? "text" : type}
            placeholder={placeholder}
            defaultValue={defaultValue}
            value={value}
            disabled={disabled}
            maxLength={maxLength}
            onInput={(e) =>
            (e.currentTarget.value =
              (type === "number" &&
                e.currentTarget.value.slice(0, maxLength)) ||
              e.currentTarget.value)
            }
            autoComplete={autoComplete ?? "off"}
            autoFocus={autoFocus}
            $hasError={hasError}
            $isPassword={type === "password"}
            $clearable={clearable}
            $height={height}
            $width={width || "100%"}
          />
        )}
      </StyledInputContainer>

      {helperText && (
        <StyledHelperTextContainer $hasError={hasError}>
          <StyledHelperTextIconContainer>※</StyledHelperTextIconContainer>
          <StyledHelperTextMessage>{helperText}</StyledHelperTextMessage>
        </StyledHelperTextContainer>
      )}

      {hasError && errorMessage && (
        <StyledErrorMessageContainer>
          <StyledErrorIconContainer>
            <ErrorIcon />
          </StyledErrorIconContainer>
          <StyledErrorMessage>{errorMessage}</StyledErrorMessage>
        </StyledErrorMessageContainer>
      )}
    </StyledContainer>
  );
};

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const StyledHeader = styled.div`
  display: flex;
  align-items: center;
`;

const StyledLabel = styled.label`
  font-size: 14px;
  font-weight: 400;
  line-height: 100%;
  margin-right: 5px;
`;

const StyledRequiredAsterisk = styled.span`
  color: ${COLOR_ORANGE};
`;

const StyledInputContainer = styled.div`
  position: relative;
`;

const StyledInput = styled.input<{
  $hasError?: boolean;
  $isPassword: boolean;
  $clearable?: boolean;
  $width?: string;
  $height?: string;
}>`
  @media (max-width: 780px) {
    font-size: 16px;
  }
  height: ${({ $height }) => $height || "44px"};
  width: ${({ $width }) => $width};
  padding: 10px 12px 10px 16px;
  border-radius: 6px;
  border: 1px solid #cccccc;
  font-size: 14px;
  line-height: 100%;
  outline: none;
  background-color: #ffffff;
  box-sizing: border-box;
  min-height: 44px; // Ensure consistent minimum height

  &::placeholder {
    color: #999999;
  }

  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  &[type="number"]::-webkit-contacts-auto-fill-button {
    display: none !important;
    visibility: hidden !important;
    pointer-events: none !important;
    position: absolute;
    right: 0;
  }

  ${({ $hasError }) =>
    $hasError &&
    `
      border-color: ${COLOR_ORANGE};
      background-color: ${COLOR_LIGHT_RED};
    `}

  &:focus {
    border-color: ${({ $hasError }) =>
    $hasError ? COLOR_ORANGE : COLOR_BLACK};
  }

  &:disabled {
    background-color: #f2f2f2;
    border-color: #cccccc;
    color: #999999;
    cursor: not-allowed;

    &::placeholder {
      color: #999999;
    }
  }
`;

const StyledHelperTextContainer = styled.div<{ $hasError?: boolean }>`
  display: flex;
  align-items: flex-start;
  ${({ $hasError }) => $hasError && `color: ${COLOR_ORANGE};`}
`;

const StyledHelperTextIconContainer = styled.div`
  margin-right: 10px;
`;

const StyledHelperTextMessage = styled.div`
  font-size: 13px;
  font-weight: 400;
  line-height: 150%;
`;

const StyledErrorMessageContainer = styled.div`
  display: flex;
  align-items: flex-end;
`;

const StyledErrorIconContainer = styled.div`
  margin: 3px;
`;

const StyledErrorMessage = styled.div`
  font-size: 14px;
  font-weight: 400;
  line-height: 150%;
  color: ${COLOR_ORANGE};
`;

const StyledTextarea = styled.textarea<{
  $hasError?: boolean;
  $width?: string;
  $height?: string;
}>`
  height: ${({ $height }) => $height};
  width: ${({ $width }) => $width};
  min-height: 96px;
  padding: 10px 12px 10px 16px;
  border-radius: 6px;
  border: 1px solid #cccccc;
  font-size: 14px;
  line-height: 150%;
  outline: none;
  background-color: #ffffff;
  resize: vertical;
  box-sizing: border-box;

  &::placeholder {
    color: #999999;
  }

  ${({ $hasError }) =>
    $hasError &&
    `
      border-color: ${COLOR_ORANGE};
      background-color: ${COLOR_LIGHT_RED};
    `}

  &:focus {
    border-color: ${({ $hasError }) =>
    $hasError ? COLOR_ORANGE : COLOR_BLACK};
  }

  &:disabled {
    background-color: #f2f2f2;
    border-color: #cccccc;
    color: #999999;
    cursor: not-allowed;

    &::placeholder {
      color: #999999;
    }
  }
`;
